import type { CSSProperties } from 'react';
import type { ComparisonPayload, ComparisonDeckStats } from '@sleeved/lib';
import type { PlayerWidgetData, PlayerPhotoData } from '../../types/widgets';
import {
  palette,
  brandGradient,
  glass,
  glassBorder,
  glassText,
  semantic,
  fonts,
} from '../../styles/overlayTokens';

// ---------------------------------------------------------------------------
// chartPrimitives — shared chart/UI primitives, constants, and helpers used
// by 2+ per-game panel modules (chronoCore.tsx, digimon.tsx, gundam.tsx,
// grandArchive.tsx) and by the CuratedPlayerPanel fallback. Split out of the
// former DeckOverlayPanels.tsx monolith; DeckOverlayPanels.tsx is now a thin
// re-export barrel — see panels/index.tsx for the game-selection registries.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// 'entering' is the perpetual settled state in normal mode — there is no separate
// idle/settled phase. Effects gated on phase === 'entering' run in steady state.
// 'static' is set when ?static=true is present; 'hidden' is the pre-mount state.
export type AnimPhase = 'hidden' | 'entering' | 'static';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function parseHex(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

// Pre-parsed RGB — all glow rgba values are derived from these, never hardcoded.
export const [P1_R, P1_G, P1_B] = parseHex(palette.coralGlow);    // #FF865C → 255, 134, 92
export const [P2_R, P2_G, P2_B] = parseHex(palette.brilliantRose); // #FF3FAF → 255, 63, 175

export const p1rgba = (alpha: number) => `rgba(${P1_R},${P1_G},${P1_B},${alpha})`;
export const p2rgba = (alpha: number) => `rgba(${P2_R},${P2_G},${P2_B},${alpha})`;

/** Relative luminance (sRGB). Used to pick pip text color. */
export function hexLuminance(hex: string): number {
  // Destructure the fixed 3-tuple BEFORE mapping — Array.prototype.map widens a
  // tuple to a plain number[], which loses the length guarantee under
  // noUncheckedIndexedAccess.
  const [rawR, rawG, rawB] = parseHex(hex);
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = toLinear(rawR);
  const g = toLinear(rawG);
  const b = toLinear(rawB);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function pipGlow(hex: string): string {
  const [r, g, b] = parseHex(hex);
  return `0 0 20px rgba(${r},${g},${b},0.55), 0 0 6px rgba(${r},${g},${b},0.30)`;
}

/**
 * Returns the correct CSS class string for animated elements.
 * - static: no animation class (element renders at natural full-opacity state)
 * - entering: apply the entry animation class
 * - hidden: apply anim-hidden (opacity 0) until entrance fires
 */
export function makeAnimCls(phase: AnimPhase) {
  return (enterClass: string): string =>
    phase === 'static' ? '' : phase === 'entering' ? enterClass : 'anim-hidden';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const gameNameMap: Record<string, string> = {
  digimon: 'Digimon Card Game',
  'chrono-core': 'Chrono Core TCG',
  'grand-archive': 'Grand Archive TCG',
  gundam: 'Gundam Card Game',
};

// Public paths for per-game logos served from packages/overlay/public/games/.
// Extensions differ per source asset: PNG for chrono-core/digimon, WebP for grand-archive/gundam.
export const gameLogoMap: Record<string, string> = {
  'chrono-core': '/games/chrono-core.png',
  'digimon': '/games/digimon.png',
  'grand-archive': '/games/grand-archive.webp',
  'gundam': '/games/gundam.webp',
};

// Fallback colors for type distribution segments that lack an adapter-supplied color.
export const TYPE_FALLBACK_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#10B981'];

// Broadcast-reliable text outline for labels that must be legible on any background color.
// White text + solid black outline (4-corner hairline + soft bloom) guarantees ≥4.5:1 edge
// contrast on both light and dark fills regardless of the underlying cell/bubble color.
// palette.onyx (#121216) is the darkest token in the overlay design system.
export const STREAM_TEXT_OUTLINE =
  `-1px -1px 0 ${palette.onyx}, 1px -1px 0 ${palette.onyx}, -1px 1px 0 ${palette.onyx}, 1px 1px 0 ${palette.onyx}, 0 0 2px ${palette.onyx}`;

export const INITIAL_COMPARISON: ComparisonPayload = { p1: null, p2: null, colorLabel: 'COLORS', gameId: '' };
export const INITIAL_PLAYER: PlayerWidgetData = {
  p1: { name: '', archetype: '' },
  p2: { name: '', archetype: '' },
  p1Score: 0,
  p2Score: 0,
  bestOf: 3,
  turnIndicator: null,
  p1Identity: null,
  p2Identity: null,
};
export const INITIAL_PHOTO: PlayerPhotoData = {};

// Canonical sort order for equipment subtypes in the Starting Loadout section.
// Items whose subtype is not in this list (or is null/undefined) sort last.

// ---------------------------------------------------------------------------
// Shared-scale helpers (computed at parent level, passed to both panels)
// ---------------------------------------------------------------------------

export function computeSharedMax(p1: ComparisonDeckStats | null, p2: ComparisonDeckStats | null): number {
  return Math.max(
    ...(p1?.costCurve ?? []).map(d => d.count),
    ...(p2?.costCurve ?? []).map(d => d.count),
    1,
  );
}

export function computeLabelUnion(p1: ComparisonDeckStats | null, p2: ComparisonDeckStats | null): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of [...(p1?.costCurve ?? []), ...(p2?.costCurve ?? [])]) {
    if (!seen.has(entry.label)) { seen.add(entry.label); out.push(entry.label); }
  }
  return out;
}

/** Union of secondaryCurve labels across both players (for Core Cost Distribution shared axis). */
export function computeCoreCostLabelUnion(p1: ComparisonDeckStats | null, p2: ComparisonDeckStats | null): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of [...(p1?.secondaryCurve ?? []), ...(p2?.secondaryCurve ?? [])]) {
    if (!seen.has(entry.label)) { seen.add(entry.label); out.push(entry.label); }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Section divider
// ---------------------------------------------------------------------------

export function SectionDivider({ margin = '20px 0' }: { margin?: string }) {
  return <div style={{ height: 1, background: glassBorder.subtle, margin, flexShrink: 0 }} />;
}

// ---------------------------------------------------------------------------
// AvatarCircle
// ---------------------------------------------------------------------------

interface AvatarCircleProps {
  photoUrl: string | null | undefined;
  name: string;
  accentColor: string;
  accentRgba: (alpha: number) => string;
  isPlaceholder: boolean;
  /** True when THIS slot has no stats but the peer slot does (one-deck state). */
  isOneDeckPlaceholder: boolean;
  /** Reduced-motion preference threaded from DeckCompareOverlay's useRef; avoids inline matchMedia calls per render. */
  reducedMotion: boolean;
}

export function AvatarCircle({ photoUrl, name, accentColor, accentRgba, isPlaceholder, isOneDeckPlaceholder, reducedMotion }: AvatarCircleProps) {
  let ringStyle: CSSProperties;
  let pulseClass = '';

  if (isOneDeckPlaceholder) {
    // One-deck state: pulsing border at 30% opacity; reduced-motion: static 0.35 opacity, no pulse.
    ringStyle = {
      width: 160,
      height: 160,
      borderRadius: '50%',
      flexShrink: 0,
      border: `3px solid ${accentColor}`,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: glassBorder.subtle,
      opacity: reducedMotion ? 0.35 : undefined,
    };
    if (!reducedMotion) pulseClass = 'dcao-placeholder-pulse';
  } else if (isPlaceholder) {
    // Zero-deck state: static, muted, no pulse.
    ringStyle = {
      width: 160,
      height: 160,
      borderRadius: '50%',
      flexShrink: 0,
      border: `3px solid ${accentColor}`,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: glassBorder.subtle,
      opacity: 0.5,
    };
  } else {
    // Normal state: full ring with glow.
    ringStyle = {
      width: 160,
      height: 160,
      borderRadius: '50%',
      flexShrink: 0,
      border: `3px solid ${accentColor}`,
      boxShadow: `0 0 24px ${accentRgba(0.50)}, 0 0 8px ${accentRgba(0.30)}`,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: glassBorder.subtle,
    };
  }

  if (photoUrl && !isPlaceholder) {
    return (
      <div className={pulseClass} style={ringStyle}>
        <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  const initial = name ? name.charAt(0).toUpperCase() : '';
  return (
    <div className={pulseClass} style={ringStyle}>
      {initial && (
        <span style={{ fontSize: 64, fontWeight: 700, color: glassText.muted, lineHeight: 1 }}>
          {initial}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ColorPips section
// ---------------------------------------------------------------------------

interface ColorPipsProps {
  dist: Array<{ label: string; count: number; color?: string }>;
  side: 'p1' | 'p2';
  phase: AnimPhase;
}

export function ColorPips({ dist, side, phase }: ColorPipsProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';

  const rowStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    justifyContent: side === 'p2' ? 'flex-end' : 'flex-start',
  };
  const pipBase: CSSProperties = {
    width: 72,
    height: 72,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (dist.length === 0) {
    return (
      <div style={rowStyle}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ ...pipBase, background: glassBorder.subtle, opacity: 0.4 }} />
        ))}
      </div>
    );
  }

  const hasOverflow = dist.length > 6;
  // When overflow: show first 5, then +N pip. Total = 6.
  const displayPips = hasOverflow ? dist.slice(0, 5) : dist;
  const overflowCount = hasOverflow ? dist.length - 5 : 0;

  return (
    <div style={rowStyle}>
      {displayPips.map((entry, idx) => {
        const hex = entry.color ?? '#888888';
        // Broadcast-white treatment: always white text + black outline so the 2-letter
        // abbreviation is legible on any pip fill color (light or dark accent).
        return (
          <div
            key={entry.label}
            data-pip=""
            className={animCls('dcao-pip-enter')}
            style={{
              ...pipBase,
              background: hex,
              boxShadow: pipGlow(hex),
              animationDelay: isEntering ? `${450 + idx * 60}ms` : undefined,
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, color: glassText.secondary, lineHeight: 1, textShadow: STREAM_TEXT_OUTLINE }}>
              {entry.label.substring(0, 2).toUpperCase()}
            </span>
          </div>
        );
      })}
      {overflowCount > 0 && (
        <div
          data-pip=""
          className={animCls('dcao-pip-enter')}
          style={{
            ...pipBase,
            background: glassBorder.subtle,
            animationDelay: isEntering ? `${450 + displayPips.length * 60}ms` : undefined,
          }}
        >
          <span style={{ fontSize: 18, fontWeight: 600, color: glassText.muted }}>
            +{overflowCount}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CostCurveChart section
// Refactored: accepts explicit accentColor prop instead of deriving from side,
// matching the established pattern of other chart sub-components (~3-line cleanup).
// ---------------------------------------------------------------------------

interface CostCurveChartProps {
  curve: Array<{ label: string; count: number }>;
  labelUnion: string[];
  sharedMax: number;
  /** Explicit accent color — caller computes from side so accent always derives from side, not layoutSide. */
  accentColor: string;
  phase: AnimPhase;
}

export function CostCurveChart({ curve, labelUnion, sharedMax, accentColor, phase }: CostCurveChartProps) {
  if (labelUnion.length === 0) return null;

  const animCls = makeAnimCls(phase);
  const countByLabel = new Map(curve.map(d => [d.label, d.count]));
  const BAR_HEIGHT = 140;

  return (
    <div
      className={animCls('dcao-bars-enter')}
      style={{
        height: BAR_HEIGHT,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 6,
        transformOrigin: 'bottom',
      }}
    >
      {labelUnion.map(label => {
        const count = countByLabel.get(label) ?? 0;
        const barH = count > 0 ? Math.max((count / sharedMax) * BAR_HEIGHT, 4) : 4;
        const isZero = count === 0;
        return (
          <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: '100%',
                height: barH,
                background: isZero ? glassBorder.subtle : accentColor,
                borderRadius: '4px 4px 0 0',
                opacity: isZero ? 0.4 : 1,
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 500, color: glassText.muted, marginTop: 8, textAlign: 'center' }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DonutTypeChart — SVG donut for Card Types distribution.
// Pure SVG, no external chart library. Used in both CuratedPlayerPanel (size=120)
// and ChronoCoreDensePanel (size=96). Replaces the horizontal proportional bar.
// ---------------------------------------------------------------------------

export const DONUT_CIRCUMFERENCE = 2 * Math.PI * 56; // ≈ 351.858

export function DonutTypeChart({
  dist,
  side,
  phase,
  size = 120,
  showTypePercentages = false,
  legendPosition,
  centerValue,
}: {
  dist: Array<{ label: string; count: number; color?: string }>;
  side: 'p1' | 'p2';
  phase: AnimPhase;
  /** Rendered pixel size of the SVG; viewBox is always 140×140. Default 120 for curated, 96 for dense. */
  size?: number;
  showTypePercentages?: boolean;
  /**
   * When 'side': force the legend beside the donut regardless of size, and mirror
   * P1 (donut-left, legend-right) vs P2 (donut-right toward outer edge, legend-left).
   * When omitted: falls back to the size threshold (≥110px = beside, <110px = below).
   * Curated panel does not pass this prop so its existing layout is unchanged.
   */
  legendPosition?: 'side' | 'below';
  /**
   * Optional count to render centered inside the donut ring.
   * Uses STREAM_TEXT_OUTLINE for legibility over any segment color.
   * Font size scales with `size` (floor: 14px). Pass null/undefined to leave center blank.
   * GA dense panel: main-deck card count from zoneFill[main].current.
   */
  centerValue?: number | null;
}) {
  if (dist.length === 0) return null;
  const total = dist.reduce((s, d) => s + d.count, 0);
  if (total === 0) return null;

  const animCls = makeAnimCls(phase);
  const isP2 = side === 'p2';

  // Pre-compute segments: each circle stroke-dasharray covers its proportional arc,
  // dashoffset positions it after the accumulated prior arcs. rotate(-90 70 70) starts at 12 o'clock.
  type Segment = { label: string; count: number; color: string; dashLength: number; gap: number; offset: number };
  const segments: Segment[] = [];
  let accumulated = 0;
  for (const entry of dist) {
    const pct = entry.count / total;
    const dashLength = pct * DONUT_CIRCUMFERENCE;
    segments.push({
      label: entry.label,
      count: entry.count,
      // `% TYPE_FALLBACK_COLORS.length` always produces an in-bounds index into a
      // non-empty, statically-sized array, so the final '' fallback is unreachable.
      color: entry.color ?? TYPE_FALLBACK_COLORS[segments.length % TYPE_FALLBACK_COLORS.length] ?? '#6366F1',
      dashLength,
      gap: DONUT_CIRCUMFERENCE - dashLength,
      offset: -accumulated * DONUT_CIRCUMFERENCE,
    });
    accumulated += pct;
  }

  // Legend placement: explicit 'side'/'below' prop takes precedence; fall back to size threshold.
  const legendBeside = legendPosition === 'side' || (legendPosition !== 'below' && size >= 110);
  // Only mirror P1/P2 when side is explicitly requested (dense panel). Curated panel uses 'row'
  // always to preserve its existing layout (legendPosition not passed → legendMirrored = false).
  const legendMirrored = legendPosition === 'side';
  const outerFlexDir: CSSProperties['flexDirection'] = legendBeside
    ? (legendMirrored && isP2 ? 'row-reverse' : 'row')
    : 'column';

  return (
    <div
      className={animCls('dcao-donut-enter')}
      style={{
        display: 'flex',
        flexDirection: outerFlexDir,
        alignItems: legendBeside ? 'center' : (isP2 ? 'flex-end' : 'flex-start'),
        gap: legendBeside ? 14 : 8,
      }}
    >
      {/* Donut SVG */}
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg
          viewBox="0 0 140 140"
          width={size}
          height={size}
          style={{ display: 'block' }}
          aria-hidden="true"
        >
          {segments.map(({ label, dashLength, gap, offset, color }) => (
            <circle
              key={label}
              cx={70}
              cy={70}
              r={56}
              fill="none"
              strokeWidth={28}
              stroke={color}
              strokeDasharray={`${dashLength} ${gap}`}
              strokeDashoffset={offset}
              transform="rotate(-90 70 70)"
            />
          ))}
        </svg>
        {/* Center value overlay (optional). Renders the supplied count centered over the ring.
            Uses STREAM_TEXT_OUTLINE for guaranteed legibility on any segment color.
            Font scales with size (min 14px). GA dense: main-deck card count. */}
        {centerValue != null && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: Math.max(14, Math.round(size * 0.22)),
            fontFamily: fonts.display,
            fontWeight: 700,
            color: glassText.primary,
            lineHeight: 1,
            textShadow: STREAM_TEXT_OUTLINE,
            pointerEvents: 'none',
          }}>
            {centerValue}
          </div>
        )}
      </div>

      {/* Legend: color swatch (8px circle) + type name + count [+ %] */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          alignItems: isP2 ? 'flex-end' : 'flex-start',
          minWidth: 0,
          flex: legendBeside ? 1 : undefined,
        }}
      >
        {segments.map(({ label, count, color }) => {
          const pct = Math.round((count / total) * 100);
          return (
            <div
              key={label}
              style={{ display: 'flex', alignItems: 'center', gap: 5, flexDirection: isP2 ? 'row-reverse' : 'row' }}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 500, color: glassText.secondary, whiteSpace: 'nowrap' }}>
                {label}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary }}>
                {count}
              </span>
              {showTypePercentages && (
                <span style={{ fontSize: 14, fontWeight: 400, color: glassText.muted }}>({pct}%)</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MiniHeader — mini section header shared across all dense-panel grid blocks.
// ---------------------------------------------------------------------------

export function MiniHeader({ text }: { text: string }) {
  return (
    <div style={{
      fontSize: 14,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: glassText.disabled,
      marginBottom: 5,
    }}>
      {text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MkDistributionBar
// ---------------------------------------------------------------------------

/** Mini proportional bar — Mk Distribution (costCurve entries sized proportionally).
 *  Replaces the lollipop chart. Segments use graduated opacity tints of the player
 *  accent color (100%/70%/45%/…). Legend: swatch + label + count.
 *  `reverse`: when true (P2 ChronoCore dense panel), segments+legend render in reversed
 *  order so P2 mirrors P1's layout. Non-mutating: spreads before reversing. */
export function MkDistributionBar({
  curve,
  side,
  accentColor,
  phase,
  reverse = false,
  legendLayout = 'inline',
}: {
  curve: Array<{ label: string; count: number }>;
  side: 'p1' | 'p2';
  accentColor: string;
  phase: AnimPhase;
  /** When true, render segments and legend in reversed order (for P2 bilateral symmetry). */
  reverse?: boolean;
  /**
   * Legend layout. 'inline' (default) wraps swatch+label+count entries in a flex-wrap row —
   * this is the existing behavior and is unchanged for all current callers (e.g. the
   * ChronoCore dense compare panel). 'list' renders each entry on its own line in a vertical
   * column — opt-in only, passed by ChronoCoreRailPanel per CD's live OBS feedback so the
   * dense panel's layout is never unintentionally affected.
   */
  legendLayout?: 'inline' | 'list';
}) {
  if (curve.length === 0) return null;
  const total = curve.reduce((s, d) => s + d.count, 0);
  if (total === 0) return null;

  const animCls = makeAnimCls(phase);
  const enterClass = side === 'p1' ? 'dcao-mini-type-p1-enter' : 'dcao-mini-type-p2-enter';
  const isP2 = side === 'p2';
  const isListLegend = legendLayout === 'list';
  // Parse accent RGB for opacity tinting — never hardcode hex values.
  const [R, G, B] = parseHex(accentColor);
  // Non-mutating reverse: spread before reversing so the shared prop array is never mutated.
  const displayCurve = reverse ? [...curve].reverse() : curve;
  // Graduated opacities across segments: 100% → 70% → 45% → floor at 25%.
  // Computed against displayCurve so the leading segment always gets full opacity.
  const opacities = displayCurve.map((_, idx) => Math.max(1.0 - idx * 0.28, 0.25));

  return (
    <>
      <div
        className={animCls(enterClass)}
        style={{ width: '100%', height: 14, borderRadius: 7, overflow: 'hidden', display: 'flex', flexDirection: 'row' }}
      >
        {displayCurve.map((entry, idx) => (
          <div
            key={entry.label}
            style={{
              flex: entry.count / total,
              height: 14,
              background: `rgba(${R},${G},${B},${opacities[idx]})`,
              minWidth: 2,
            }}
          />
        ))}
      </div>
      <div style={{
        display: 'flex',
        flexDirection: isListLegend ? 'column' : 'row',
        flexWrap: isListLegend ? undefined : 'wrap',
        gap: isListLegend ? 4 : '4px 8px',
        marginTop: 8,
        justifyContent: isListLegend ? undefined : (isP2 ? 'flex-end' : 'flex-start'),
      }}>
        {displayCurve.map((entry, idx) => (
          <div
            key={entry.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              // 'list' legend is single-column, p1-only (see legendLayout doc above) — no
              // mirroring needed there; 'inline' still mirrors for P2 bilateral symmetry.
              flexDirection: isListLegend ? 'row' : (isP2 ? 'row-reverse' : 'row'),
            }}
          >
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: `rgba(${R},${G},${B},${opacities[idx]})`,
              flexShrink: 0,
            }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: glassText.secondary, whiteSpace: 'nowrap' }}>
              {entry.label}
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary }}>
              {entry.count}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// MiniBarChart
// ---------------------------------------------------------------------------

/** Mini bar chart — Core Cost Distribution (secondaryCurve).
 *  Count labels appear above each bar when count > 0. Shared scale across both players.
 *  `reverse`: when true (P2 ChronoCore dense panel), bars+labels render right→left
 *  descending so P2 mirrors P1's ascending layout. Non-mutating: spreads before reversing. */
export function MiniBarChart({
  curve,
  labelUnion,
  sharedMax,
  accentColor,
  phase,
  reverse = false,
  compact = false,
}: {
  curve: Array<{ label: string; count: number }>;
  labelUnion: string[];
  sharedMax: number;
  accentColor: string;
  phase: AnimPhase;
  /** When true, render labels/bars in reversed order (for P2 bilateral symmetry). */
  reverse?: boolean;
  /**
   * When true, reduces chart dimensions for the condensed rail layout:
   * chart height 50 (vs 62), label height 14 (vs 18), axis marginTop 3 (vs 4).
   * Font sizes are NOT reduced in compact mode — count label is 14 and axis tick is
   * 16 in all modes (tiered text floor: standard=14, stream-critical axis=16).
   * Backward-compatible: existing callers without this prop get default (false) full-size behavior.
   */
  compact?: boolean;
}) {
  if (labelUnion.length === 0) return null;
  const animCls = makeAnimCls(phase);
  const countByLabel = new Map(curve.map(d => [d.label, d.count]));
  // CHART_H reduced from 72→62 (full) or 50 (compact) to compensate for the marginTop added on
  // the Core Cost cell wrapper (keeps Core Cost cell total height net-neutral while clearing the
  // header overlap). compact=true is used by ChronoCoreRailPanel where space is tighter.
  const CHART_H = compact ? 50 : 62;
  const LABEL_H = compact ? 14 : 18; // fixed height reserved above bars for count labels
  // Non-mutating reverse: spread before reversing so the shared prop array is never mutated.
  const displayLabels = reverse ? [...labelUnion].reverse() : labelUnion;

  return (
    <div
      className={animCls('dcao-mini-bars-enter')}
      style={{
        height: CHART_H + LABEL_H,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 4,
        transformOrigin: 'bottom',
      }}
    >
      {displayLabels.map(label => {
        const count = countByLabel.get(label) ?? 0;
        const isZero = count === 0;
        const barH = isZero ? 2 : Math.max((count / sharedMax) * CHART_H, 2);
        return (
          <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Count label area (fixed LABEL_H) — only rendered when count > 0 */}
            <div style={{ height: LABEL_H, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 2 }}>
              {count > 0 && (
                // D95 P2: count label — 14 for all modes (was `compact ? 12 : 14`); standard text floor
                <span style={{ fontSize: 14, fontWeight: 500, color: glassText.muted, lineHeight: 1 }}>
                  {count}
                </span>
              )}
            </div>
            <div style={{
              width: '100%',
              height: barH,
              borderRadius: '3px 3px 0 0',
              background: isZero ? glassBorder.subtle : accentColor,
              opacity: isZero ? 0.4 : 1,
            }} />
            {/* D95 P2: axis tick — 16 for all modes (was `compact ? 12 : 14`); stream-critical label */}
            <span style={{ fontSize: 14, fontWeight: 500, color: glassText.disabled, marginTop: compact ? 3 : 4, textAlign: 'center' }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KeywordBubbleCloud
// ---------------------------------------------------------------------------

/** Keyword bubble cloud — size and opacity of each bubble scale with count.
 *  Replaces the 2-column keyword grid in ChronoCoreDensePanel.
 *  fontSize: 14→18px; bubble alpha: 0.5→1.0 (raised floor for legibility at low counts).
 *  Player accent color for brand cohesion. */
export function KeywordBubbleCloud({
  keywords,
  side,
  accentColor,
  phase,
  isEntering,
  startDelay,
  compact = false,
}: {
  keywords: Array<{ label: string; count: number }>;
  side: 'p1' | 'p2';
  accentColor: string;
  phase: AnimPhase;
  isEntering: boolean;
  startDelay: number;
  /**
   * When true, reduces bubble padding (3px/8px vs 4px/10px) to pack more keywords into
   * a vertically constrained container (e.g. ChronoCoreRailPanel). Default false —
   * backward-compatible: existing callers without this prop get full-size padding.
   * NOTE: keyword bubbles are a frequency-scaled word cloud sized 12→14px
   * (fontSize = 12 + ratio*2) — a small tier below the 14px body floor, by design.
   */
  compact?: boolean;
}) {
  if (keywords.length === 0) return null;

  const animCls = makeAnimCls(phase);
  const isP2 = side === 'p2';
  const maxCount = Math.max(...keywords.map(kw => kw.count), 1);
  const [R, G, B] = parseHex(accentColor);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 4 : 6, justifyContent: isP2 ? 'flex-end' : 'flex-start' }}>
      {keywords.map((kw, idx) => {
        const ratio = kw.count / maxCount;
        // Keyword word cloud capped at 12→14px (CD directive): least-common keyword 12px,
        // most-common 14px — kept at/below the section-header size so hot keywords don't
        // out-size the headers. compact prop still affects padding (3px/8px vs 4px/10px).
        const fontSize = 12 + ratio * 2;
        const alpha = 0.5 + ratio * 0.5;          // 0.5→1.0 (raised floor — was 0.35 — ensures readable backing at low counts)

        // Broadcast-white treatment: always white text + black outline so keyword labels are
        // legible on any bubble accent color. Eliminates luminance-threshold flip to dark text.
        return (
          <div
            key={kw.label}
            className={animCls('dcao-kw-enter')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: compact ? '3px 8px' : '4px 10px',
              borderRadius: 9999,
              background: `rgba(${R},${G},${B},${alpha})`,
              animationDelay: isEntering ? `${startDelay + idx * 30}ms` : undefined,
            }}
          >
            <span style={{ fontSize, fontWeight: 600, color: glassText.secondary, lineHeight: 1, textShadow: STREAM_TEXT_OUTLINE }}>
              {kw.label}
            </span>
            {/* Count: broadcast-white + outline for legibility on any background (STREAM_TEXT_OUTLINE) */}
            <span style={{ fontSize, fontWeight: 600, color: glassText.secondary, lineHeight: 1, textShadow: STREAM_TEXT_OUTLINE }}>
              {kw.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MiniHorizontalBars
// ---------------------------------------------------------------------------

/** Mini horizontal bar rows — Trait Breakdown and Equipment Subtype. */
export function MiniHorizontalBars({
  entries,
  localMax,
  side,
  accentColor,
  phase,
  isEntering,
  startDelay,
}: {
  entries: Array<{ label: string; count: number }>;
  localMax: number;
  side: 'p1' | 'p2';
  accentColor: string;
  phase: AnimPhase;
  isEntering: boolean;
  startDelay: number;
}) {
  const isP2 = side === 'p2';
  const animCls = makeAnimCls(phase);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {entries.map((entry, idx) => (
        <div key={entry.label} style={{
          display: 'flex',
          flexDirection: isP2 ? 'row-reverse' : 'row',
          alignItems: 'center',
          gap: 6,
          height: 20,
        }}>
          {/* Trait label */}
          <span style={{
            fontSize: 14,
            fontWeight: 500,
            color: glassText.secondary,
            whiteSpace: 'nowrap',
            width: 72,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 0,
            textAlign: isP2 ? 'right' : 'left',
          }}>
            {entry.label}
          </span>
          {/* Bar track */}
          <div style={{ flex: 1, height: 6, borderRadius: 3, background: glassBorder.medium, position: 'relative', overflow: 'hidden' }}>
            <div
              className={animCls('dcao-trait-bar-enter')}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                borderRadius: 3,
                background: accentColor,
                width: `${localMax > 0 ? (entry.count / localMax) * 100 : 0}%`,
                transformOrigin: isP2 ? 'right' : 'left',
                animationDelay: isEntering ? `${startDelay + idx * 30}ms` : undefined,
              }}
            />
          </div>
          {/* Count */}
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: accentColor,
            width: 22,
            flexShrink: 0,
            textAlign: isP2 ? 'left' : 'right',
          }}>
            {entry.count}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MiniHeatmap
// ---------------------------------------------------------------------------

/** Mini heatmap — Damage Value and Charge Value distributions (additionalCurves).
 *  Intensity overlay uses opacity variation of accent color; count number is always
 *  shown inside the cell so information is preserved in forced-colors mode. */
export function MiniHeatmap({
  data,
  sharedMax,
  accentColor,
  phase,
  isEntering,
  startDelay,
}: {
  data: Array<{ label: string; count: number }>;
  sharedMax: number;
  accentColor: string;
  phase: AnimPhase;
  isEntering: boolean;
  startDelay: number;
}) {
  const animCls = makeAnimCls(phase);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'flex-end' }}>
      {data.map((entry, idx) => {
        // Broadcast-white treatment: always white text + black outline so count labels are
        // legible on any cell intensity (dim or fully saturated accent fill).
        // Eliminates luminance-threshold flip to dark text.
        const intensity = sharedMax > 0 ? entry.count / sharedMax : 0;

        return (
        <div key={entry.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Cell body with intensity overlay */}
          <div
            className={animCls('dcao-heatmap-enter')}
            style={{
              width: '100%',
              height: 28,
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
              background: glassBorder.medium,
              animationDelay: isEntering ? `${startDelay + idx * 30}ms` : undefined,
            }}
          >
            {/* Opacity-based intensity — flagged for accessibility-auditor: WCAG 1.4.11
                concern on forced-colors; count number inside mitigates information loss. */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: accentColor,
              opacity: intensity,
              borderRadius: 4,
            }} />
            {/* Count label inside cell — broadcast-white + outline (STREAM_TEXT_OUTLINE) */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 14,
              fontWeight: 600,
              color: glassText.secondary,
              textShadow: STREAM_TEXT_OUTLINE,
            }}>
              {entry.count}
            </div>
          </div>
          {/* Value label below cell */}
          <span style={{ fontSize: 14, fontWeight: 500, color: glassText.disabled, marginTop: 4, textAlign: 'center' }}>
            {entry.label}
          </span>
        </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ZoneDot — kept for grand-archive zone fill rows
// ---------------------------------------------------------------------------

interface ZoneDotProps {
  side: 'p1' | 'p2';
  zone: { current: number; max: number } | undefined;
  phase: AnimPhase;
  delay: string | undefined;
}

export function ZoneDot({ side, zone, phase, delay }: ZoneDotProps) {
  const animCls = makeAnimCls(phase);

  let dotColor: string;
  if (!zone) {
    dotColor = glassBorder.medium;
  } else if (zone.current >= zone.max) {
    dotColor = side === 'p1' ? palette.coralGlow : palette.brilliantRose;
  } else if (zone.current > 0) {
    dotColor = semantic.warning;
  } else {
    dotColor = glassBorder.medium;
  }

  return (
    <div
      className={animCls('dcao-dot-enter')}
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: dotColor,
        flexShrink: 0,
        animationDelay: delay,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// VS Center Zone
// ---------------------------------------------------------------------------

interface VSCenterProps {
  comp: ComparisonPayload;
  phase: AnimPhase;
  /** True for chrono-core, digimon, gundam — hide zone-fill rows; false for grand-archive etc. */
  hideZoneFill: boolean;
}

export function VSCenter({ comp, phase, hideZoneFill }: VSCenterProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const { p1, p2 } = comp;
  const bothNull = p1 === null && p2 === null;

  // Union of zone labels from whichever deck(s) are loaded (still needed for grand-archive)
  const zoneRows = (() => {
    const p1Zones = p1?.zoneFill ?? [];
    const p2Zones = p2?.zoneFill ?? [];
    const allLabels = new Map<string, { label: string; p1?: { current: number; max: number }; p2?: { current: number; max: number } }>();
    for (const z of p1Zones) {
      allLabels.set(z.zoneId, { label: z.label, p1: { current: z.current, max: z.max } });
    }
    for (const z of p2Zones) {
      const existing = allLabels.get(z.zoneId);
      if (existing) {
        existing.p2 = { current: z.current, max: z.max };
      } else {
        allLabels.set(z.zoneId, { label: z.label, p2: { current: z.current, max: z.max } });
      }
    }
    return Array.from(allLabels.values()).slice(0, 5);
  })();

  const gradientLine: CSSProperties = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 1,
    background: `linear-gradient(to bottom, transparent 0%, ${palette.coralGlow} 30%, ${palette.brilliantRose} 70%, transparent 100%)`,
    opacity: 0,
  };

  return (
    <div
      style={{
        flex: '0 0 360px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 8px',
        position: 'relative',
        gap: 0,
      }}
    >
      {/* Vertical separator lines */}
      <div className={animCls('dcao-sep-enter')} style={{ ...gradientLine, left: 8 }} />
      <div className={animCls('dcao-sep-enter')} style={{ ...gradientLine, right: 8 }} />

      {/* VS logotype — dcao-vs-logotype is STABLE (for forced-colors CSS rule);
          dcao-vs-enter is the entrance animation class.
          Forced-colors fallback: see dcao-vs-logotype rule in animations.css.
          Flagged for accessibility-auditor: WCAG 1.4.11 forced-colors compliance. */}
      <div
        className={`dcao-vs-logotype ${animCls('dcao-vs-enter')}`}
        style={{
          fontSize: 140,
          fontWeight: 900,
          lineHeight: 1.0,
          textAlign: 'center',
          background: brandGradient.horizontal,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          WebkitTextStroke: '1px rgba(18,18,22,0.25)',
          marginBottom: 28,
        }}
      >
        VS
      </div>

      {/* Zone fill section — gated by game:
          grand-archive keeps full zone-fill rows; chrono-core/digimon/gundam hide them.
          ZoneDot and semantic import are kept since grand-archive still uses them. */}
      {hideZoneFill ? (
        /* Simplified waiting fallback for games without zone fill rows */
        bothNull ? (
          <div style={{
            fontSize: 14,
            fontWeight: 400,
            color: glassText.disabled,
            fontStyle: 'italic',
            textAlign: 'center',
            marginTop: 20,
          }}>
            Waiting for decks
          </div>
        ) : null
      ) : (
        /* Full zone fill rows for grand-archive and other games */
        bothNull ? (
          <div style={{ fontSize: 16, fontWeight: 400, color: glassText.muted, fontStyle: 'italic', textAlign: 'center' }}>
            Waiting for decks
          </div>
        ) : zoneRows.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            {zoneRows.map((zone, idx) => {
              const dotDelay = isEntering ? `${700 + idx * 40}ms` : undefined;
              return (
                <div key={zone.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  {/* P1 side */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ZoneDot side="p1" zone={zone.p1} phase={phase} delay={dotDelay} />
                    {zone.p1 && (
                      <span style={{ fontSize: 14, fontWeight: 500, color: glassText.secondary }}>
                        {zone.p1.current}/{zone.p1.max}
                      </span>
                    )}
                  </div>
                  {/* Zone label */}
                  <span style={{ fontSize: 14, fontWeight: 500, color: glassText.muted, textAlign: 'center', flex: 1 }}>
                    {zone.label}
                  </span>
                  {/* P2 side */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: 'row-reverse' }}>
                    <ZoneDot side="p2" zone={zone.p2} phase={phase} delay={dotDelay} />
                    {zone.p2 && (
                      <span style={{ fontSize: 14, fontWeight: 500, color: glassText.secondary }}>
                        {zone.p2.current}/{zone.p2.max}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CuratedPlayerPanel (renamed from PlayerPanel — curated 4-section layout for
// Digimon, Grand Archive, Gundam and any game other than ChronoCore)
// ---------------------------------------------------------------------------

export interface CuratedPlayerPanelProps {
  side: 'p1' | 'p2';
  stats: ComparisonDeckStats | null;
  /** True when the opposing slot has stats — used to distinguish one-deck vs zero-deck placeholder. */
  peerHasStats: boolean;
  playerName: string;
  archetype: string;
  photoUrl: string | null | undefined;
  sharedMax: number;
  labelUnion: string[];
  colorLabel: string;
  phase: AnimPhase;
  /** Reduced-motion preference threaded from DeckCompareOverlay; passed on to AvatarCircle. */
  reducedMotion: boolean;
  /**
   * When true, the panel expands to fill available flex space (max-width 960px, centered via
   * margin auto). Default (false/absent): fixed 680px flex basis for the compare overlay.
   */
  fullWidth?: boolean;
  /**
   * When true, all DIRECTION logic resolves to left-to-right (P1) orientation regardless of
   * `side`. ACCENT color continues to derive from `side`. Used by the profile widget to render
   * P2's deck in a single-panel left-to-right layout without changing the P2 accent color.
   */
  naturalLayout?: boolean;
  /**
   * Override for the generic no-stats, no-name placeholder string.
   * Falls back to 'Load a deck in the caster console to see stats' when absent.
   */
  placeholderText?: string;
}

export function CuratedPlayerPanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  photoUrl,
  sharedMax,
  labelUnion,
  colorLabel,
  phase,
  reducedMotion,
  fullWidth,
  naturalLayout,
  placeholderText,
}: CuratedPlayerPanelProps) {
  const animCls = makeAnimCls(phase);
  const isP1 = side === 'p1';
  // naturalLayout: direction logic resolves to P1 orientation; accent stays keyed on side (isP1).
  const isP1Layout = isP1 || !!naturalLayout;
  const layoutSide: 'p1' | 'p2' = isP1Layout ? 'p1' : side;
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasName = playerName.trim().length > 0;
  const hasStats = stats !== null;
  // One-deck state: this slot is empty but the other slot has a deck loaded.
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  const panelEnterClass = isP1Layout ? 'dcao-p1-enter' : 'dcao-p2-enter';

  return (
    <div
      className={animCls(panelEnterClass)}
      style={{
        flex: fullWidth ? 1 : '0 0 680px',
        maxWidth: fullWidth ? 1400 : undefined,
        margin: fullWidth ? '0 auto' : undefined,
        height: '100%',
        position: 'relative',
        background: glass.card,
        borderRadius: 16,
        border: `1px solid ${glassBorder.light}`,
        overflow: 'hidden',
        // isP1Layout governs which edge gets the accent border and which direction the inset shadow runs.
        // accentColor/accentRgba remain keyed on isP1 (side) for color identity.
        // fullWidth: directional side accent border + inner-edge shadow removed — no "toward-center" edge exists.
        ...(isP1Layout
          ? { borderRight: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset -4px 0 20px ${accentRgba(0.12)}` }
          : { borderLeft: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset 4px 0 20px ${accentRgba(0.12)}` }),
      }}
    >
      {/* Top accent strip */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          borderRadius: '16px 16px 0 0',
          background: accentColor,
        }}
      />

      {/* Inner content column — id used by content-update effect */}
      <div
        id={`dcao-content-${side}`}
        style={{
          position: 'absolute',
          top: 4,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '32px 36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
          alignItems: isP1Layout ? 'flex-start' : 'flex-end',
        }}
      >
        {/* Player identity zone */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            flexDirection: isP1Layout ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 20,
            height: 180,
            marginBottom: 28,
            width: '100%',
            flexShrink: 0,
          }}
        >
          {/* Avatar */}
          <AvatarCircle
            photoUrl={photoUrl}
            name={playerName}
            accentColor={accentColor}
            accentRgba={accentRgba}
            isPlaceholder={!hasStats}
            isOneDeckPlaceholder={isOneDeckPlaceholder}
            reducedMotion={reducedMotion}
          />

          {/* Name block */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0,
              flex: 1,
              alignItems: isP1Layout ? 'flex-start' : 'flex-end',
              textAlign: isP1Layout ? 'left' : 'right',
            }}
          >
            {hasName ? (
              <div
                style={{
                  fontSize: 52,
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  color: glassText.primary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.0,
                  marginBottom: 6,
                  width: '100%',
                }}
              >
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 18, fontWeight: 400, color: glassText.muted, marginBottom: 6 }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}

            {archetype && (
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 500,
                  color: glassText.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: 4,
                  width: '100%',
                }}
              >
                {archetype}
              </div>
            )}

            {/* Archetype fallback: one-deck → "Awaiting deck..."; zero-deck → "No deck loaded" */}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.disabled, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : 'No deck loaded'}
              </div>
            )}
          </div>
        </div>

        {/* Stats sections — or placeholder when no stats */}
        {hasStats ? (
          <>
            <SectionDivider />

            {/* Color identity */}
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: glassText.muted,
                marginBottom: 14,
              }}
            >
              {colorLabel || 'COLORS'}
            </div>
            {/* layoutSide governs pip justifyContent direction */}
            <ColorPips dist={stats.colorDistribution} side={layoutSide} phase={phase} />

            <SectionDivider />

            {/* Cost curve */}
            {stats.costCurve.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: glassText.muted,
                    marginBottom: 10,
                  }}
                >
                  {stats.costCurveLabel || 'COST CURVE'}
                </div>
                {/* accentColor derived from side (isP1), not layoutSide */}
                <CostCurveChart
                  curve={stats.costCurve}
                  labelUnion={labelUnion}
                  sharedMax={sharedMax}
                  accentColor={accentColor}
                  phase={phase}
                />
                <SectionDivider />
              </>
            )}

            {/* Type distribution */}
            {stats.typeDistribution.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: glassText.muted,
                    marginBottom: 10,
                  }}
                >
                  CARD TYPES
                </div>
                {/* layoutSide governs legend alignment direction */}
                <DonutTypeChart dist={stats.typeDistribution} side={layoutSide} phase={phase} size={120} />
              </>
            )}
          </>
        ) : (
          /* No-stats placeholder */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 12,
            }}
          >
            {/* Deck icon SVG */}
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ opacity: 0.4 }}>
              <rect x="4" y="8" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
              <rect x="8" y="4" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
            </svg>
            <div
              style={{
                fontSize: 16,
                fontWeight: 400,
                color: glassText.muted,
                textAlign: 'center',
                maxWidth: 240,
              }}
            >
              {hasName
                ? 'Awaiting deck…'
                : (placeholderText ?? 'Load a deck in the caster console to see stats')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── D92: New sub-components ───────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// BinaryDistributionBar — proportional split bar for two named segments.
// Used by Gundam (Zone Composition / binaryDistribution) and Grand Archive
// (Speed Distribution / speedDistribution). Segment colors use entry.color when
// present; fall back to fallbackColors[0/1] when absent (caller supplies
// overlayTokens references — never raw hex).
// Accessibility note: flagged for accessibility-auditor review on WCAG 1.4.11
// (forced-colors — both segments degrade; legend labels preserve information).
// ---------------------------------------------------------------------------

export interface BinaryDistributionBarProps {
  /** Exactly 2 entries — segment A and segment B. */
  entries: Array<{ label: string; count: number; color?: string }>;
  /** Fallback colors applied when entry.color is absent. Must be overlayTokens references. */
  fallbackColors: [string, string];
  phase: AnimPhase;
  isEntering: boolean;
  startDelay: number;
}

export function BinaryDistributionBar({
  entries,
  fallbackColors,
  phase,
  isEntering,
  startDelay,
}: BinaryDistributionBarProps) {
  if (entries.length < 2) return null;
  // `entries.length >= 2` guarantees both elements exist.
  const [a, b] = entries as [BinaryDistributionBarProps['entries'][number], BinaryDistributionBarProps['entries'][number]];
  const total = a.count + b.count;
  if (total === 0) return null;

  const colorA = a.color ?? fallbackColors[0];
  const colorB = b.color ?? fallbackColors[1];
  const animCls = makeAnimCls(phase);

  return (
    <>
      {/* Proportional bar */}
      <div
        className={`dcao-binary-bar ${animCls('dcao-trait-bar-enter')}`.trim()}
        style={{
          width: '100%',
          height: 18,
          borderRadius: 9,
          overflow: 'hidden',
          display: 'flex',
          animationDelay: isEntering ? `${startDelay}ms` : undefined,
        }}
      >
        <div className="dcao-binary-bar-sep" style={{ flex: a.count, height: 18, background: colorA, minWidth: a.count > 0 ? 4 : 0 }} />
        <div style={{ flex: b.count, height: 18, background: colorB, minWidth: b.count > 0 ? 4 : 0 }} />
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 12, marginTop: 8 }}>
        {([{ entry: a, color: colorA }, { entry: b, color: colorB }] as const).map(({ entry, color }) => (
          <div key={entry.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: glassText.secondary }}>{entry.label}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary }}>{entry.count}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers (D92) — used by Digimon, Gundam, and Grand Archive dense/rail
// panels. Originally file-private; exported here for cross-module use after the
// panels/ split (intra-package export only — behavior unchanged).
// ---------------------------------------------------------------------------

/** Single-circle featured-card avatar for Digimon + Gundam dense and rail panels.
 * Plain bordered circle — do NOT use StackedCardAvatars (which is a stacked pair). */
export function FeaturedCardAvatarCircle({
  imageUrl,
  circleSize,
  accentColor,
  accentRgba,
  isOneDeckPlaceholder,
  reducedMotion,
}: {
  imageUrl: string | null | undefined;
  circleSize: number;
  accentColor: string;
  accentRgba: (alpha: number) => string;
  isOneDeckPlaceholder: boolean;
  reducedMotion: boolean;
}) {
  const hasImage = imageUrl != null && imageUrl !== '';

  if (isOneDeckPlaceholder) {
    return (
      <div
        className={reducedMotion ? undefined : 'dcao-placeholder-pulse'}
        style={{
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          border: `3px solid ${accentColor}`,
          background: glassBorder.subtle,
          flexShrink: 0,
          overflow: 'hidden',
          opacity: reducedMotion ? 0.35 : undefined,
        }}
      />
    );
  }

  if (hasImage) {
    return (
      <div style={{
        width: circleSize,
        height: circleSize,
        borderRadius: '50%',
        border: `3px solid ${accentColor}`,
        boxShadow: `0 0 16px ${accentRgba(0.40)}`,
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        <img
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- `hasImage` (checked by the enclosing `if` above) is exactly `imageUrl != null && imageUrl !== ''`, so `imageUrl` is guaranteed non-null/non-empty here.
          src={imageUrl!}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 15%',
            display: 'block',
            transform: 'scale(1.5)',
            transformOrigin: 'center 15%',
          }}
        />
      </div>
    );
  }

  // Zero-deck or no-URL: static muted circle
  return (
    <div style={{
      width: circleSize,
      height: circleSize,
      borderRadius: '50%',
      border: `3px solid ${accentColor}`,
      background: glassBorder.subtle,
      flexShrink: 0,
      overflow: 'hidden',
      opacity: 0.5,
    }} />
  );
}

/** Dense panel no-stats placeholder — shared deck icon + text pattern. */
export function DensePanelPlaceholder({
  hasName,
  placeholderText,
}: {
  hasName: boolean;
  placeholderText: string | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 12 }}>
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ opacity: 0.4 }}>
        <rect x="4" y="8" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
        <rect x="8" y="4" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
      </svg>
      <div style={{ fontSize: 16, fontWeight: 400, color: glassText.muted, textAlign: 'center', maxWidth: 240 }}>
        {hasName ? 'Awaiting deck...' : (placeholderText ?? 'Load a deck in the caster console to see stats')}
      </div>
    </div>
  );
}

